<template>
  <v-col>
    <v-alert
      v-if="!canUseDevApi"
      type="warning"
      outlined
      class="mb-4"
    >
      {{ accessWarningMessage }}
    </v-alert>

    <v-card
      v-if="canUseDevApi"
      class="mb-4"
    >
      <v-card-title>API Tester</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <label class="field-label">Bearer Token</label>
            <v-text-field
              v-model="token"
              placeholder="Paste your bearer token here..."
              :disabled="!canUseDevApi"
              outlined
              dense
              hide-details
              class="mono-input"
            />
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col
            cols="12"
            sm="2"
          >
            <label class="field-label">Method</label>
            <v-select
              v-model="method"
              :items="methods"
              :disabled="!canUseDevApi"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="8"
          >
            <label class="field-label">Endpoint</label>
            <v-text-field
              v-model="endpoint"
              placeholder="/master_data/publishers"
              :disabled="!canUseDevApi"
              outlined
              dense
              hide-details
              class="mono-input"
              @keydown.enter="sendRequest"
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
            class="d-flex align-end"
          >
            <v-btn
              outlined
              class="text-none"
              color="primary"
              :loading="loading"
              :disabled="!canUseDevApi"
              block
              @click="sendRequest"
            >
              <font-awesome-icon
                :icon="['fas', 'paper-plane']"
                size="sm"
                class="mr-2"
              />
              Send
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Status + Timing -->
    <div
      v-if="statusText"
      class="mb-2"
    >
      <span
        :class="statusOk ? 'success--text' : 'error--text'"
        class="font-weight-bold"
      >
        {{ statusText }}
      </span>
      <span
        v-if="elapsed"
        class="ml-3 grey--text text--darken-1"
      >
        {{ elapsed }}ms round-trip
      </span>
    </div>

    <!-- Metadata Panels -->
    <v-row
      v-if="hasMetadata"
      class="mb-4"
    >
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          dark
          color="#263238"
          class="meta-panel"
        >
          <v-card-subtitle class="meta-title pb-1">
            Request
          </v-card-subtitle>
          <v-card-text class="meta-body">
            <div
              v-for="row in requestMeta"
              :key="row.key"
              class="meta-row"
            >
              <span class="meta-key">{{ row.key }}</span>
              <span class="meta-val">{{ row.val }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          dark
          color="#263238"
          class="meta-panel"
        >
          <v-card-subtitle class="meta-title pb-1">
            Response
          </v-card-subtitle>
          <v-card-text class="meta-body">
            <div
              v-for="row in responseMeta"
              :key="row.key"
              class="meta-row"
            >
              <span class="meta-key">{{ row.key }}</span>
              <span class="meta-val">{{ row.val }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Response Body -->
    <v-card
      v-if="responseBody !== null"
      dark
      color="#1e1e1e"
    >
      <v-card-text class="response-body">
        {{ responseBody }}
      </v-card-text>
    </v-card>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const BASE = 'https://beta.api.iadops.com'
const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'DevApi',

  data() {
    return {
      token: '',
      method: 'GET',
      methods: ['GET', 'POST', 'PATCH', 'DELETE', 'HEAD'],
      endpoint: '/master_data/publishers',
      loading: false,
      statusText: '',
      statusOk: false,
      elapsed: null,
      requestMeta: [],
      responseMeta: [],
      responseBody: null,
    }
  },

  computed: {
    ...mapAuthGetters([
      'isUserInternalAdmin',
    ]),

    canUseDevApi() {
      return process.env.NODE_ENV !== 'production' && this.isUserInternalAdmin
    },

    accessWarningMessage() {
      return 'This tool is available only to internal admin users in non-production environments.'
    },

    hasMetadata() {
      return this.requestMeta.length > 0 || this.responseMeta.length > 0
    },
  },

  created() {
    if (this.canUseDevApi) {
      this.token = this.$store.state.auth.idToken || ''
    }
  },

  methods: {
    async sendRequest() {
      if (!this.canUseDevApi) {
        this.statusText = 'Unauthorized'
        this.statusOk = false
        return
      }

      const { token, method, endpoint } = this

      if (!token || !endpoint) return

      this.loading = true
      this.statusText = ''
      this.elapsed = null
      this.responseMeta = []
      this.responseBody = null

      const url = `${BASE}${endpoint}`
      const reqHeaders = {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      }

      // Build request metadata
      const truncatedToken = `Bearer ${token.slice(0, 12)}...${token.slice(-6)}`
      this.requestMeta = [
        { key: 'Method', val: method },
        { key: 'URL', val: url },
        { key: 'Authorization', val: truncatedToken },
        { key: 'Accept', val: 'application/json' },
      ]

      const t0 = performance.now()

      try {
        const res = await fetch(url, { method, headers: reqHeaders })

        this.elapsed = (performance.now() - t0).toFixed(0)
        this.statusText = `${res.status} ${res.statusText}`
        this.statusOk = res.ok

        // Build response metadata
        const resMeta = [
          { key: 'Status', val: this.statusText },
          { key: 'Redirected', val: String(res.redirected) },
          { key: 'Type', val: res.type },
        ]

        res.headers.forEach((v, k) => {
          resMeta.push({ key: k, val: v })
        })

        const contentType = res.headers.get('content-type') || ''

        if (contentType.includes('json')) {
          const json = await res.json()
          const bodySize = JSON.stringify(json).length
          resMeta.push({ key: 'Body size', val: `${(bodySize / 1024).toFixed(1)} KB (${bodySize} bytes)` })

          if (Array.isArray(json)) {
            resMeta.push({ key: 'Row count', val: String(json.length) })
          }

          this.responseBody = JSON.stringify(json, null, 2)
        } else {
          const text = await res.text()
          resMeta.push({ key: 'Body size', val: `${(text.length / 1024).toFixed(1)} KB (${text.length} bytes)` })
          this.responseBody = text
        }

        this.responseMeta = resMeta
      } catch (err) {
        this.elapsed = (performance.now() - t0).toFixed(0)
        this.statusText = 'Request failed'
        this.statusOk = false
        this.responseBody = err.message
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}

.mono-input ::v-deep input {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 13px;
}

.meta-panel {
  max-height: 260px;
  overflow: auto;
}

.meta-title {
  color: #80cbc4 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #37474f;
}

.meta-body {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 12px;
  padding-top: 4px !important;
}

.meta-row {
  display: flex;
  gap: 8px;
  padding: 2px 0;
}

.meta-key {
  color: #4fc3f7;
  min-width: 160px;
  flex-shrink: 0;
}

.meta-val {
  color: #e0e0e0;
  word-break: break-all;
}

.response-body {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 70vh;
  overflow: auto;
  color: #d4d4d4;
}
</style>
